// /src/pages/gro/QuestionnairesPage.tsx
import { useEffect, useMemo, useState } from "react";
import { Download, FileText, Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CrudRowActions } from "@/components/shared/CrudRowActions";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  useImportQuestionnaireModelToClient,
  useQuestionnaires,
} from "@/hooks/useQuestionnaires";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import type { QuestionnaireScope } from "@/services/questionnaires";

type StatusFilter = "active" | "inactive" | "all";

type QuestionnairesPageProps = {
  scope?: QuestionnaireScope;
  basePath?: string;
  title?: string;
  description?: string;
  showImportModels?: boolean;
};

export default function QuestionnairesPage({
  scope = "client",
  basePath = "/gro/questionnaires",
  title = "Questionários",
  description = "Cadastre, versione e publique questionários do módulo GRO.",
  showImportModels = true,
}: QuestionnairesPageProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const readPermission =
    scope === "tenant_model"
      ? PERMISSIONS.TENANT_MODELS_QUESTIONNAIRES_READ
      : PERMISSIONS.QUESTIONNAIRES_READ;
  const writePermission =
    scope === "tenant_model"
      ? PERMISSIONS.TENANT_MODELS_QUESTIONNAIRES_WRITE
      : PERMISSIONS.QUESTIONNAIRES_WRITE;
  const canRead = user?.permissions?.includes(readPermission) ?? false;
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const questionnairesQ = useQuestionnaires({
    include_inactive: statusFilter !== "active",
    q: debouncedSearch || undefined,
    scope,
  });

  const templateQuestionnairesQ = useQuestionnaires({
    include_inactive: false,
    scope: "tenant_model",
  });

  const importMut = useImportQuestionnaireModelToClient();

  const items = useMemo(() => {
    const data = questionnairesQ.data ?? [];
    if (statusFilter === "inactive") {
      return data.filter((item) => !item.is_active);
    }
    return data;
  }, [questionnairesQ.data, statusFilter]);

  if (!canRead) {
    return (
      <EmptyState
        icon={FileText}
        title="Sem acesso ao módulo"
        description="Você não possui permissão para visualizar questionários."
      />
    );
  }

  async function handleImportModel(questionnaireId: string) {
    try {
      const imported = await importMut.mutateAsync(questionnaireId);
      toast.success("Modelo importado para o cliente selecionado.");
      setImportOpen(false);
      navigate(`${basePath}/${imported.id}`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Erro ao importar modelo.");
    }
  }

  return (
    <div>
      <PageHeader title={title} description={description}>
        <div className="flex flex-wrap gap-2">
          {showImportModels && scope === "client" ? (
            <PermissionButton
              permission={writePermission}
              variant="outline"
              onClick={() => setImportOpen(true)}
            >
              <Download className="mr-2 h-4 w-4" />
              Importar modelo
            </PermissionButton>
          ) : null}
          <PermissionButton
            permission={writePermission}
            onClick={() => navigate(`${basePath}/novo`)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo questionário
          </PermissionButton>
        </div>
      </PageHeader>

      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center">
          <div className="relative w-full max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Buscar por código, nome ou descrição"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="w-full md:w-[220px]">
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as StatusFilter)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="inactive">Inativos</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {items.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="Nenhum questionário encontrado"
            description={
              scope === "client"
                ? "Importe um modelo da consultoria ou crie um questionário específico para este cliente."
                : "Crie o primeiro modelo de questionário da consultoria."
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Questionário</TableHead>
                  <TableHead>Versões</TableHead>
                  <TableHead>Publicada</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[120px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-xs">{item.code}</TableCell>
                    <TableCell>
                      <div className="font-medium">{item.name}</div>
                      {item.description ? (
                        <div className="text-sm text-muted-foreground">
                          {item.description}
                        </div>
                      ) : null}
                      {item.source_questionnaire_id ? (
                        <Badge variant="outline" className="mt-2">
                          Importado de modelo
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>{item.versions_count}</TableCell>
                    <TableCell>
                      {item.published_version_number ? (
                        <Badge variant="secondary">
                          v{item.published_version_number}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Sem publicação
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={item.is_active ? "default" : "outline"}>
                        {item.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <CrudRowActions
                        onView={() => navigate(`${basePath}/${item.id}`)}
                        onEdit={() => navigate(`${basePath}/${item.id}/editar`)}
                        viewPermission={readPermission}
                        editPermission={writePermission}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Importar modelo da consultoria</DialogTitle>
            <DialogDescription>
              Escolha um modelo do tenant para copiar para o cliente selecionado.
              Depois de importado, o questionário do cliente passa a ser independente.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[420px] overflow-y-auto rounded-lg border">
            {(templateQuestionnairesQ.data ?? []).length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">
                Nenhum modelo ativo disponível na consultoria.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Modelo</TableHead>
                    <TableHead className="w-[140px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(templateQuestionnairesQ.data ?? []).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-xs">{item.code}</TableCell>
                      <TableCell>
                        <div className="font-medium">{item.name}</div>
                        {item.description ? (
                          <div className="text-sm text-muted-foreground">
                            {item.description}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          onClick={() => handleImportModel(item.id)}
                          disabled={importMut.isPending}
                        >
                          Importar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
